import React, { useEffect, useRef } from 'react';

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  pulsePhase: number;
  pulseSpeed: number;
  connections: number[];
}

interface TravelingPulse {
  fromIdx: number;
  toIdx: number;
  progress: number;    // 0 to 1
  speed: number;       // progress per frame
  opacity: number;
}

const NetworkBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const nodesRef = useRef<Node[]>([]);
  const mouseRef = useRef<{ x: number; y: number }>({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ACCENT = { r: 91, g: 110, b: 245 }; // #5b6ef5 — logo purple
    const ACCENT_LIGHT = { r: 139, g: 158, b: 248 }; // #8b9ef8
    const NODE_COUNT = 60;
    const CONNECTION_DISTANCE = 200;
    const MOUSE_RADIUS = 150;
    const BASE_SPEED = 0.15;

    function resize() {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    // Initialize nodes
    const nodes: Node[] = [];
    for (let i = 0; i < NODE_COUNT; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * BASE_SPEED * 2,
        vy: (Math.random() - 0.5) * BASE_SPEED * 2,
        radius: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.01 + 0.005,
        connections: [],
      });
    }
    nodesRef.current = nodes;

    // Mouse tracking
    function handleMouseMove(e: MouseEvent) {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    }
    function handleMouseLeave() {
      mouseRef.current = { x: -1000, y: -1000 };
    }
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    let time = 0;
    const pulses: TravelingPulse[] = [];

    function spawnPulse() {
      // Find a valid connection to pulse along
      for (let attempt = 0; attempt < 10; attempt++) {
        const i = Math.floor(Math.random() * nodes.length);
        const j = Math.floor(Math.random() * nodes.length);
        if (i === j) continue;
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < CONNECTION_DISTANCE * 0.8) {
          pulses.push({
            fromIdx: i,
            toIdx: j,
            progress: 0,
            speed: 0.008 + Math.random() * 0.012,
            opacity: 0.7 + Math.random() * 0.3,
          });
          return;
        }
      }
    }

    function animate() {
      if (!canvas || !ctx) return;
      time++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const mouse = mouseRef.current;

      // Update node positions
      for (const node of nodes) {
        // Gentle pulse
        node.pulsePhase += node.pulseSpeed;

        // Drift movement
        node.x += node.vx;
        node.y += node.vy;

        // Mouse repulsion (subtle)
        const dx = node.x - mouse.x;
        const dy = node.y - mouse.y;
        const distToMouse = Math.sqrt(dx * dx + dy * dy);
        if (distToMouse < MOUSE_RADIUS && distToMouse > 0) {
          const force = (1 - distToMouse / MOUSE_RADIUS) * 0.3;
          node.vx += (dx / distToMouse) * force;
          node.vy += (dy / distToMouse) * force;
        }

        // Speed dampening
        node.vx *= 0.99;
        node.vy *= 0.99;

        // Enforce minimum drift
        const speed = Math.sqrt(node.vx * node.vx + node.vy * node.vy);
        if (speed < BASE_SPEED * 0.5) {
          node.vx += (Math.random() - 0.5) * 0.02;
          node.vy += (Math.random() - 0.5) * 0.02;
        }

        // Wrap around edges with padding
        const pad = 50;
        if (node.x < -pad) node.x = canvas.width + pad;
        if (node.x > canvas.width + pad) node.x = -pad;
        if (node.y < -pad) node.y = canvas.height + pad;
        if (node.y > canvas.height + pad) node.y = -pad;
      }

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < CONNECTION_DISTANCE) {
            const alpha = (1 - dist / CONNECTION_DISTANCE) * 0.15;

            // Glow near mouse
            const midX = (nodes[i].x + nodes[j].x) / 2;
            const midY = (nodes[i].y + nodes[j].y) / 2;
            const distMidMouse = Math.sqrt(
              (midX - mouse.x) ** 2 + (midY - mouse.y) ** 2
            );
            const mouseGlow = distMidMouse < MOUSE_RADIUS * 1.5
              ? (1 - distMidMouse / (MOUSE_RADIUS * 1.5)) * 0.3
              : 0;

            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(${ACCENT.r}, ${ACCENT.g}, ${ACCENT.b}, ${alpha + mouseGlow})`;
            ctx.lineWidth = 0.5 + mouseGlow * 2;
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      for (const node of nodes) {
        const pulse = Math.sin(node.pulsePhase) * 0.3 + 0.7;
        const r = node.radius * pulse;
        const alpha = node.opacity * pulse;

        // Mouse proximity glow
        const dx = node.x - mouse.x;
        const dy = node.y - mouse.y;
        const distToMouse = Math.sqrt(dx * dx + dy * dy);
        const mouseBoost = distToMouse < MOUSE_RADIUS
          ? (1 - distToMouse / MOUSE_RADIUS) * 0.6
          : 0;

        // Outer glow
        if (r > 1.5 || mouseBoost > 0.1) {
          const glowRadius = r * 3 + mouseBoost * 8;
          const gradient = ctx.createRadialGradient(
            node.x, node.y, 0,
            node.x, node.y, glowRadius
          );
          gradient.addColorStop(0, `rgba(${ACCENT.r}, ${ACCENT.g}, ${ACCENT.b}, ${(alpha + mouseBoost) * 0.3})`);
          gradient.addColorStop(1, `rgba(${ACCENT.r}, ${ACCENT.g}, ${ACCENT.b}, 0)`);
          ctx.beginPath();
          ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
          ctx.fillStyle = gradient;
          ctx.fill();
        }

        // Core dot
        ctx.beginPath();
        ctx.arc(node.x, node.y, r + mouseBoost * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${ACCENT.r}, ${ACCENT.g}, ${ACCENT.b}, ${Math.min(alpha + mouseBoost, 1)})`;
        ctx.fill();
      }

      // Spawn new traveling pulses periodically
      if (time % 45 === 0) {
        spawnPulse();
      }

      // Update and draw traveling pulses
      for (let p = pulses.length - 1; p >= 0; p--) {
        const pulse = pulses[p];
        pulse.progress += pulse.speed;

        if (pulse.progress >= 1) {
          pulses.splice(p, 1);
          continue;
        }

        const from = nodes[pulse.fromIdx];
        const to = nodes[pulse.toIdx];

        // Check if the connection still exists
        const dx = from.x - to.x;
        const dy = from.y - to.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > CONNECTION_DISTANCE * 1.2) {
          pulses.splice(p, 1);
          continue;
        }

        const px = from.x + (to.x - from.x) * pulse.progress;
        const py = from.y + (to.y - from.y) * pulse.progress;

        // Fade in/out at edges
        const edgeFade = Math.min(pulse.progress * 4, (1 - pulse.progress) * 4, 1);
        const alpha = pulse.opacity * edgeFade;

        // Glow trail
        const glowRadius = 12;
        const gradient = ctx.createRadialGradient(px, py, 0, px, py, glowRadius);
        gradient.addColorStop(0, `rgba(${ACCENT_LIGHT.r}, ${ACCENT_LIGHT.g}, ${ACCENT_LIGHT.b}, ${alpha * 0.6})`);
        gradient.addColorStop(0.4, `rgba(${ACCENT.r}, ${ACCENT.g}, ${ACCENT.b}, ${alpha * 0.3})`);
        gradient.addColorStop(1, `rgba(${ACCENT.r}, ${ACCENT.g}, ${ACCENT.b}, 0)`);
        ctx.beginPath();
        ctx.arc(px, py, glowRadius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Bright core
        ctx.beginPath();
        ctx.arc(px, py, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${ACCENT_LIGHT.r}, ${ACCENT_LIGHT.g}, ${ACCENT_LIGHT.b}, ${alpha})`;
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0, opacity: 0.6 }}
    />
  );
};

export default NetworkBackground;
